import { useAtom, useSetAtom } from 'jotai';
import { loadable } from 'jotai/utils';
import { savedViewsAtom, filteredSavedViewsAtom, removeSavedViewAtom, refreshSavedViewsAtom } from '~/atoms/savedViews';
import { showToastAtom } from '~/atoms/pageActions';
import { useModalActions } from '~/atoms/pageActions';
import { useMemo, useCallback } from 'react';
import { groupSavedDataByList, filterAndSortProjects } from '~/utils/transformSavedViewData';
import { deleteSavedView } from '~/service/savedViewService';
const loadableAtom = loadable(savedViewsAtom);
const loadableFilteredAtom = loadable(filteredSavedViewsAtom);
export const useSavedViews = (useFiltered = false, currentUserEmail, activeViewId) => {
    const [savedViews] = useAtom(loadableAtom);
    const [filteredSavedViews] = useAtom(loadableFilteredAtom);
    const removeSavedView = useSetAtom(removeSavedViewAtom);
    const { openModal } = useModalActions();
    const showToast = useSetAtom(showToastAtom);
    const [, setRefreshTrigger] = useAtom(refreshSavedViewsAtom);
    const refreshSavedViews = useCallback(() => {
        setRefreshTrigger((prev) => !prev);
    }, [setRefreshTrigger]);
    const viewsToUse = useFiltered ? filteredSavedViews : savedViews;
    const { savedViewGroupedByList, savedViewDataSortedAndFiltered } = useMemo(() => {
        if (viewsToUse.state === 'hasData' && viewsToUse.data?.length > 0) {
            return {
                savedViewGroupedByList: groupSavedDataByList(viewsToUse.data),
                savedViewDataSortedAndFiltered: filterAndSortProjects(viewsToUse.data)
            };
        }
        return {
            savedViewGroupedByList: [],
            savedViewDataSortedAndFiltered: []
        };
    }, [viewsToUse]);
    const menuReadySavedViews = useMemo(() => {
        return savedViewGroupedByList.map((group) => ({
            listName: group.listName,
            savedViews: group.savedViews.map((view) => ({
                projectId: view.projectId,
                name: view.name,
                canDelete: currentUserEmail
                    ? currentUserEmail === view.createdBy
                    : false,
                isActive: activeViewId === view.projectId
            }))
        }));
    }, [savedViewGroupedByList, currentUserEmail, activeViewId]);
    const openDeleteSavedViewModal = useCallback(({ viewId, viewName }) => {
        const handleDeleteSavedViewConfirm = async () => {
            try {
                await deleteSavedView(viewId);
                removeSavedView(viewId);
                showToast({
                    message: `Saved view ${viewName} deleted successfully.`,
                    type: 'success'
                });
            }
            catch {
                showToast({
                    message: `Failed to delete saved view ${viewName}. Please try again.`,
                    type: 'critical'
                });
            }
        };
        openModal({
            modalText: `Are you sure you want to delete view ${viewName}?`,
            buttonText: 'Delete',
            action: handleDeleteSavedViewConfirm
        });
    }, [openModal, showToast, removeSavedView]);
    return {
        savedViews: savedViewDataSortedAndFiltered,
        savedViewsByList: savedViewGroupedByList,
        menuReadySavedViews,
        savedViewsLoadingState: viewsToUse.state,
        openDeleteSavedViewModal,
        refreshSavedViews
    };
};
